import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import { Clock, BarChart3, ArrowRight, GraduationCap } from "lucide-react";
import { useQuote } from "@/components/QuoteDialog";
import { trainings, images } from "@/mock/mock";

const levelColor = {
  Foundation: "text-brand-cyan border-brand-cyan/30 bg-brand-cyan/5",
  Intermediate: "text-brand-lime border-brand-lime/30 bg-brand-lime/5",
  Advanced: "text-brand-orange border-brand-orange/30 bg-brand-orange/5",
};

export default function Training() {
  const { openQuote } = useQuote();
  return (
    <>
      <PageHeader
        eyebrow="Training"
        title="Build a security-first culture with expert-led training"
        subtitle="In 90% of incidents, evidence is damaged before responders arrive. Our courses help teams prevent, detect and respond — delivered in-house or virtually across Canada."
      />

      <section className="py-20">
        <div className="mx-auto max-w-7xl px-6 grid lg:grid-cols-2 gap-14 items-center mb-16">
          <Reveal>
            <div className="rounded-2xl overflow-hidden border border-white/10">
              <img src={images.training} alt="Corporate training session" className="w-full h-[380px] object-cover" />
            </div>
          </Reveal>
          <Reveal delay={100}>
            <span className="text-sm font-semibold uppercase tracking-wider text-brand-lime">Hands-on learning</span>
            <h2 className="mt-3 font-display text-3xl font-bold text-white">Practical courses drawn from real investigations</h2>
            <p className="mt-4 text-slate-400">Our instructors bring years of forensic and incident-response experience into every session, blending theory with realistic labs your team can apply immediately.</p>
            <Button onClick={openQuote} className="group mt-7 bg-brand-orange hover:bg-brand-orangeDark text-white font-semibold rounded-full px-7 h-12">
              Enquire about a cohort<ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Button>
          </Reveal>
        </div>

        <div className="mx-auto max-w-7xl px-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {trainings.map((t, i) => (
            <Reveal key={t.title} delay={(i % 3) * 70}>
              <div className="h-full rounded-2xl border border-white/10 bg-brand-navy2/60 p-7 card-hover">
                <div className="flex items-center justify-between">
                  <div className="grid h-11 w-11 place-items-center rounded-xl bg-brand-lime/10 text-brand-lime">
                    <GraduationCap className="h-5 w-5" />
                  </div>
                  <span className={`rounded-full border px-3 py-1 text-[11px] font-medium ${levelColor[t.level]}`}>{t.level}</span>
                </div>
                <h3 className="mt-5 font-display text-lg font-semibold text-white">{t.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{t.desc}</p>
                <div className="mt-5 flex items-center gap-4 text-xs text-slate-400">
                  <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-brand-lime" /> {t.duration}</span>
                  <span className="flex items-center gap-1.5"><BarChart3 className="h-3.5 w-3.5 text-brand-lime" /> {t.level}</span>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </>
  );
}
